import { z } from 'zod';
import { RoleSchema } from '../schemas/common.schema.js';

/**
 * "Who they can see": which PEOPLE a role's holders may read, on top of what its
 * permissions let them do. ALL is everyone; SITE_WORKERS is site labour only —
 * not office staff, not remote people (the SELF_ANYWHERE profile);
 * SUPPLIED_WORKERS is agency manpower only (employmentType AGENCY);
 * SITE_AND_SUPPLIED_WORKERS is either of the two together. A system
 * role is always ALL.
 */
export const AccessWorkforceScopeSchema = z.enum(['ALL', 'SITE_WORKERS', 'SUPPLIED_WORKERS', 'SITE_AND_SUPPLIED_WORKERS']);
export type AccessWorkforceScope = z.infer<typeof AccessWorkforceScopeSchema>;

export const AccessRoleInputSchema = z.object({
  name: z.string().trim().min(2).max(60),
  description: z.string().trim().max(240).nullish(),
  /** The coarse class the role behaves as wherever code still asks "is this a manager?". */
  baseRole: RoleSchema,
  permissions: z.array(z.string().max(80)).max(500),
  /** Absent keeps what the role has (ALL on a new one). */
  workforceScope: AccessWorkforceScopeSchema.optional(),
});
export type AccessRoleInput = z.infer<typeof AccessRoleInputSchema>;

export const AccessRoleAssignSchema = z.object({
  /** null puts the person back on the built-in role their base role names. */
  accessRoleId: z.string().nullable(),
});
export type AccessRoleAssign = z.infer<typeof AccessRoleAssignSchema>;

export interface AccessRoleDto {
  id: string;
  key: string;
  name: string;
  description: string | null;
  baseRole: z.infer<typeof RoleSchema>;
  isSystem: boolean;
  customized: boolean;
  permissions: string[];
  /** Always ALL on a system role. */
  workforceScope: AccessWorkforceScope;
  userCount: number;
}
